import { formatLabNumber } from '@quickreport/domain';
import { kvGet, kvSet, type LocalDb, type Tx } from '@quickreport/sync';

/**
 * Lab numbers come from runs the server reserved for this device, so two computers never
 * hand out the same number, even when both are offline. The runs live only on this device.
 */
export interface NumberBlock {
  blockId: string;
  siteId: string;
  year: number;
  first: number;
  last: number;
  /** The next number this device will use. */
  next: number;
}

const PREFIX = 'nb:';
type Q = Pick<LocalDb, 'query'> | Tx;

export class NoLabNumbersError extends Error {
  constructor() {
    super('This computer has run out of lab numbers. Connect to the internet to get more.');
    this.name = 'NoLabNumbersError';
  }
}

/** Numbers restart every calendar year, in Indian time. */
export function istYear(d: Date = new Date()): number {
  return Number(
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric' }).format(d),
  );
}

export async function listBlocks(db: Q, orgId: string): Promise<NumberBlock[]> {
  const r = await db.query<{ value: string }>(
    `select value from sync_state where org_id = $1 and key like '${PREFIX}%'`,
    [orgId],
  );
  return r.rows.map((x) => JSON.parse(x.value) as NumberBlock);
}

export async function addBlock(
  db: Q,
  orgId: string,
  b: { blockId: string; siteId: string; year: number; first: number; last: number },
): Promise<void> {
  const block: NumberBlock = { ...b, next: b.first };
  await kvSet(db, `${PREFIX}${b.blockId}`, JSON.stringify(block), orgId);
}

export async function remaining(
  db: Q,
  orgId: string,
  siteId: string,
  now: Date = new Date(),
): Promise<number> {
  const year = istYear(now);
  return (await listBlocks(db, orgId))
    .filter((b) => b.siteId === siteId && b.year === year)
    .reduce((n, b) => n + Math.max(0, b.last - b.next + 1), 0);
}

/** Take the next number and format it. Call inside the transaction that saves the report. */
export async function takeLabNumber(
  tx: Q,
  orgId: string,
  site: { id: string; code: string; pattern: string },
  now: Date = new Date(),
): Promise<string> {
  const year = istYear(now);
  const blocks = (await listBlocks(tx, orgId))
    .filter((b) => b.siteId === site.id && b.year === year && b.next <= b.last)
    .sort((a, b) => a.first - b.first);
  const block = blocks[0];
  if (!block) throw new NoLabNumbersError();
  const seq = block.next;
  await kvSet(tx, `${PREFIX}${block.blockId}`, JSON.stringify({ ...block, next: seq + 1 }), orgId);
  return formatLabNumber(site.pattern, { seq, date: now, site: site.code });
}

export type BlockRequest = (
  siteId: string,
  count: number,
) => Promise<{ blockId: string; siteId: string; year: number; first: number; last: number }>;

/** Ask the server for another run when this device is getting low. Safe to call often. */
export async function topUpNumbers(
  db: LocalDb,
  orgId: string,
  siteId: string,
  request: BlockRequest,
  opts: { low?: number; count?: number; now?: Date } = {},
): Promise<boolean> {
  const left = await remaining(db, orgId, siteId, opts.now);
  if (left >= (opts.low ?? 100)) return false;
  const b = await request(siteId, opts.count ?? 500);
  await addBlock(db, orgId, b);
  return true;
}

export async function hasKey(db: Q, orgId: string, key: string): Promise<boolean> {
  return (await kvGet(db, key, orgId)) !== null;
}
